import React from "react";
import { View, Text, StyleSheet } from "react-native";
import Svg, { Path } from "react-native-svg";
import { useTheme } from "../../../context/ThemeContext";
import { useMobile } from "../../../hooks/useMobile";
import { Reveal, WebClass, FONT_DISPLAY, FONT_MONO } from "./_shared";

// Vertical SVG glyphs — match v4 strokes 1:1.
function IndustryIcon({ kind, color }) {
  const c = { stroke: color, strokeWidth: 2, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" };
  switch (kind) {
    case "business":
      return (
        <Svg width={24} height={24} viewBox="0 0 24 24">
          <Path {...c} d="M3 21V7l9-4 9 4v14M9 21V11M15 21V11" />
        </Svg>
      );
    case "maritime":
      return (
        <Svg width={24} height={24} viewBox="0 0 24 24">
          <Path {...c} d="M2 21c1 0 1-1 3-1s2 1 4 1 2-1 4-1 2 1 4 1 2-1 3-1M3 18l3-8h12l3 8M12 3v7" />
        </Svg>
      );
    case "gov":
      return (
        <Svg width={24} height={24} viewBox="0 0 24 24">
          <Path {...c} d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </Svg>
      );
    default:
      return null;
  }
}

const INDUSTRIES = [
  {
    kind: "business", iconBg: "rgba(37,112,189,0.14)", iconBorder: "rgba(37,112,189,0.3)", iconColorKey: "brand2", primary: true,
    title: "Small & Mid-size Business",
    desc: "Self-serve cloud PBX with AI-guided setup. Replace your legacy phone system in an afternoon.",
    bullets: ["Per-seat pricing", "Softphone + bring-your-own SIP devices", "Number porting + new DID provisioning"],
  },
  {
    kind: "maritime", iconBg: "rgba(76,183,130,0.12)", iconBorder: "rgba(76,183,130,0.3)", iconColorKey: "green",
    title: "Maritime & Cruise",
    desc: "Onboard PBX with multi-extension domain support, crew calling cards, and integration with shipboard PMS.",
    bullets: ["Multi-extension per cabin", "Calling cards & rate plans", "Battle-tested on cruise & naval deployments"],
  },
  {
    kind: "gov", iconBg: "rgba(241,178,80,0.12)", iconBorder: "rgba(241,178,80,0.3)", iconColorKey: "orange",
    title: "Government & Defense",
    desc: "Hardened deployments with E911, recording compliance, and air-gapped on-prem options.",
    bullets: ["NT E911 service integration", "Call recording & retention policies", "FedRAMP-aligned controls"],
  },
];

export default function NtVoxcraftIndustries() {
  const { theme } = useTheme();
  const isMobile = useMobile();

  return (
    <View nativeID="industries" style={[styles.section, { paddingVertical: isMobile ? 64 : 110 }]}>
      <View style={styles.container}>
        <Reveal>
          <View style={styles.head}>
            <Text style={[styles.eyebrow, { color: theme.textMute }]}>BUILT FOR YOUR VERTICAL</Text>
            <Text style={[styles.title, { color: theme.text }, isMobile && styles.titleMobile]}>One platform.{"\n"}Three industries that depend on it.</Text>
            <Text style={[styles.lede, { color: theme.textDim }]}>
              We started in maritime. The platform's grown to serve small business, government, and onboard
              cruise operations, without sacrificing the specialization any of them need.
            </Text>
          </View>
        </Reveal>

        <View style={[styles.grid, isMobile && styles.gridMobile]}>
          {INDUSTRIES.map((it, i) => (
            <View key={i} style={[styles.cell, isMobile && styles.cellMobile]}>
              <Reveal delay={i + 1}>
                <WebClass
                  className="voxcraft-ind-card"
                  style={[
                    styles.card,
                    { borderColor: theme.borderSoft, backgroundColor: theme.surface },
                    it.primary && { borderColor: theme.pillBorder },
                  ]}
                >
                  {it.primary && (
                    <View style={[styles.primaryTag, { backgroundColor: theme.brand2 }]}>
                      <Text style={styles.primaryTagText}>PRIMARY</Text>
                    </View>
                  )}
                  <View style={[styles.iconBox, { backgroundColor: it.iconBg, borderColor: it.iconBorder }]}>
                    <IndustryIcon kind={it.kind} color={theme[it.iconColorKey] || theme.brand2} />
                  </View>
                  <Text style={[styles.cardTitle, { color: theme.text }]}>{it.title}</Text>
                  <Text style={[styles.cardDesc, { color: theme.textDim }]}>{it.desc}</Text>
                  <View style={[styles.bullets, { borderTopColor: theme.borderSoft }]}>
                    {it.bullets.map((b, j) => (
                      <View key={j} style={styles.bulletRow}>
                        <View style={[styles.bulletDot, { backgroundColor: theme.brand2 }]} />
                        <Text style={[styles.bulletText, { color: theme.textDim }]}>{b}</Text>
                      </View>
                    ))}
                  </View>
                </WebClass>
              </Reveal>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  section: { paddingHorizontal: 28 },
  container: { maxWidth: 1280, marginHorizontal: "auto", width: "100%" },
  head: { maxWidth: 760, marginBottom: 56 },
  eyebrow: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 1.6, marginBottom: 12, textTransform: "uppercase" },
  title: { fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: "800", letterSpacing: -1, lineHeight: 46, marginBottom: 16 },
  titleMobile: { fontSize: 32, lineHeight: 36 },
  lede: { fontSize: 17, lineHeight: 27 },

  grid: { flexDirection: "row", flexWrap: "wrap", marginHorizontal: -9 },
  gridMobile: { flexDirection: "column", marginHorizontal: 0 },
  cell: { width: "33.33%", padding: 9 },
  cellMobile: { width: "100%", padding: 0, paddingBottom: 12 },

  card: { padding: 28, borderRadius: 12, borderWidth: 1, height: "100%", position: "relative", overflow: "hidden" },
  // Inside the card, top-right corner — matches v4 ind-tag.
  primaryTag: {
    position: "absolute", top: 16, right: 16,
    paddingHorizontal: 9, paddingVertical: 4, borderRadius: 999,
  },
  primaryTagText: { color: "#fff", fontSize: 9, fontWeight: "800", letterSpacing: 0.7 },
  iconBox: { width: 50, height: 50, borderRadius: 11, borderWidth: 1, alignItems: "center", justifyContent: "center", marginBottom: 18 },
  cardTitle: { fontFamily: FONT_DISPLAY, fontSize: 19, fontWeight: "700", marginBottom: 10, letterSpacing: -0.2 },
  cardDesc: { fontSize: 13.5, lineHeight: 21, marginBottom: 18, minHeight: 60 },
  bullets: { borderTopWidth: 1, paddingTop: 14 },
  bulletRow: { flexDirection: "row", alignItems: "center", paddingVertical: 4 },
  bulletDot: { width: 5, height: 5, borderRadius: 3, marginRight: 8 },
  bulletText: { fontSize: 12.5 },
});
